import { useInclinicStore } from '@/store';
import { useCallback, useEffect } from 'react';
import { CLIENT_ENV } from '@/config/configuration';
import { AuthService } from '@davincihealthcare/auth-client';
import { useIsWelfare } from '@/app/_hooks/useIsWelfare';
import { Patient } from '@/types/patient';

const authService = AuthService.initialize({
  baseUrl: CLIENT_ENV().NEXT_PUBLIC_AUTH_URL!,
  appId: CLIENT_ENV().NEXT_PUBLIC_AUTH_APP_ID!,
});

// Flag to ensure patient is fetched only once per session
let isPatientFetched = false;

export const usePatient = () => {
  const { patient, isPatientLoading, setPatient, updatePatient, clearPatient, setPatientLoading } = useInclinicStore();
  const isWelfare = useIsWelfare();

  const fetchPatient = useCallback(async () => {
    try {
      await authService.waitForInitialization();

      const token = authService.currentToken();

      if (!token) {
        setPatient(null);

        return;
      }

      setPatientLoading(true);

      const response = await fetch(`${CLIENT_ENV().NEXT_PUBLIC_PATIENT_BACKEND_URL}/patients/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const patient = (await response.json()) as Patient | null;

      setPatient(patient);
    } catch (error) {
      console.error('Errore nel recuperare i dati utente:', error);
      setPatient(null);
    } finally {
      setPatientLoading(false);
    }
  }, [setPatient, setPatientLoading]);

  useEffect(() => {
    if (isWelfare) {
      clearPatient();

      return;
    }

    if (isPatientFetched) return;

    fetchPatient();

    isPatientFetched = true;
  }, [isWelfare, fetchPatient, clearPatient]);

  return {
    patient,
    isPatientLoading,
    setPatient,
    updatePatient,
    clearPatient,
    setPatientLoading,
    fetchPatient,
  };
};
